var _excluded = ["children"],
  _excluded2 = ["attachRef", "children", "className", "closeButton", "dataTestId", "onClose", "open", "returnFocus", "title", "zIndex"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import classnames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import FocusLock, { MoveFocusInside } from 'react-focus-lock';
import { useTheme } from 'styled-components';
import { StyledCloseButton, StyledDialog, StyledDialogMask } from "./styled";
import BackgroundProvider from "../BackgroundProvider";
import { useBrand } from "../BrandProvider";
import Heading from "../Heading";
import { Close } from "../Icon";
import { uniqueId } from "../../utils";
var WrappedFocusLock = function WrappedFocusLock(_ref) {
  var children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded);
  var isIE = (typeof document === "undefined" ? "undefined" : _typeof(document)) === 'object' && document.documentMode;
  if (isIE) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, children);
  }
  return /*#__PURE__*/React.createElement(FocusLock, rest, children);
};

/**
 * Documentation:
 *
 * - [Link for Windows (On-prem)](https://pages.ghe.service.group/constellation/docs/components/dialog)
 **/
var Dialog = function Dialog(_ref2) {
  var _ref2$attachRef = _ref2.attachRef,
    attachRef = _ref2$attachRef === void 0 ? {} : _ref2$attachRef,
    children = _ref2.children,
    customClass = _ref2.className,
    _ref2$closeButton = _ref2.closeButton,
    closeButton = _ref2$closeButton === void 0 ? true : _ref2$closeButton,
    dataTestId = _ref2.dataTestId,
    _ref2$onClose = _ref2.onClose,
    onClose = _ref2$onClose === void 0 ? function () {} : _ref2$onClose,
    _ref2$open = _ref2.open,
    open = _ref2$open === void 0 ? false : _ref2$open,
    _ref2$returnFocus = _ref2.returnFocus,
    returnFocus = _ref2$returnFocus === void 0 ? true : _ref2$returnFocus,
    title = _ref2.title,
    _ref2$zIndex = _ref2.zIndex,
    zIndex = _ref2$zIndex === void 0 ? 1000 : _ref2$zIndex,
    rest = _objectWithoutProperties(_ref2, _excluded2);
  var brandContext = useBrand();
  var _ref3 = useBrand() || {},
    _ref3$testAttributeNa = _ref3.testAttributeName,
    testAttributeName = _ref3$testAttributeNa === void 0 ? 'data-qa-id' : _ref3$testAttributeNa;
  var testAttributes;
  var getTestAttributes = function getTestAttributes(element) {
    var _testAttributes;
    return ((_testAttributes = testAttributes) === null || _testAttributes === void 0 ? void 0 : _testAttributes[element]) || {};
  };
  if (dataTestId) {
    testAttributes = {};
    testAttributes.default = _defineProperty({}, testAttributeName, dataTestId);
    testAttributes.closeButton = _defineProperty({}, testAttributeName, "".concat(dataTestId, "-close-button"));
    testAttributes.title = _defineProperty({}, testAttributeName, "".concat(dataTestId, "-title"));
  }
  var el = useRef();
  var titleId = useRef(uniqueId('csl-dialog-title'));
  var descriptionId = useRef(uniqueId('csl-dialog-desc'));
  var theme = useTheme();
  var transitionEnd = function transitionEnd() {
    if (el.current && el.current.ariaHidden === 'true') {
      el.current.style.visibility = 'hidden';
    }
  };
  useEffect(function () {
    return function () {
      return el.current && el.current.removeEventListener('transitionend', transitionEnd);
    };
  }, []);
  var handleCloseButtonClick = function handleCloseButtonClick() {
    var rootEl = document.getElementById('root');
    if (rootEl) rootEl.removeAttribute('aria-hidden');
    if (typeof onClose === 'function') onClose();
  };
  var handleKeyDown = function handleKeyDown(e) {
    if (e.keyCode === 27 && typeof onClose === 'function') onClose();
  };
  var handleMaskClick = function handleMaskClick(e) {
    if (e.target === el.current && typeof onClose === 'function') onClose();
  };
  var attachRefs = useCallback(function (ref) {
    if (typeof attachRef === 'function') attachRef(ref);else {
      attachRef.current = ref;
      el.current = ref;
      transitionEnd();
      if (el.current) el.current.addEventListener('transitionend', transitionEnd);
    }
  }, []);
  useEffect(function () {
    var rootEl = document.getElementById('root');
    if (open) {
      document.documentElement.classList.add('csl-dialog-open');
      if (rootEl) rootEl.setAttribute('aria-hidden', 'true');
    }
    window.addEventListener('keydown', handleKeyDown);
    return function cleanup() {
      window.removeEventListener('keydown', handleKeyDown);
      document.documentElement.classList.remove('csl-dialog-open');
      if (rootEl) rootEl.removeAttribute('aria-hidden');
    };
  }, []);
  useEffect(function () {
    var rootEl = document.getElementById('root');
    if (open) {
      document.documentElement.classList.add('csl-dialog-open');
      if (rootEl) rootEl.setAttribute('aria-hidden', 'true');
      if (el.current) el.current.style.visibility = 'visible';
    } else {
      document.documentElement.classList.remove('csl-dialog-open');
      if (rootEl) rootEl.removeAttribute('aria-hidden');
    }
  }, [open]);
  var className = useMemo(function () {
    return classnames('csl-dialog', {
      'csl-dialog--closed': !open
    });
  }, [open]);
  var dialogClassName = useMemo(function () {
    return classnames('csl-dialog__dialog', customClass, {
      'csl-dialog--no-close-button': !closeButton,
      'csl-dialog--no-title': !title
    });
  }, [closeButton, customClass, title]);
  if (!brandContext || !brandContext.portalEl) {
    return null;
  }
  return /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(StyledDialogMask, {
    $open: open,
    $zIndex: zIndex,
    "aria-hidden": !open,
    className: className,
    hidden: !open,
    onClick: handleMaskClick,
    onKeyDown: handleKeyDown
  }, /*#__PURE__*/React.createElement(WrappedFocusLock, {
    disabled: !open,
    returnFocus: returnFocus
  }, open && /*#__PURE__*/React.createElement(MoveFocusInside, null, /*#__PURE__*/React.createElement(BackgroundProvider, {
    value: {
      cssValue: theme.vars.dialog_color_background
    }
  }, /*#__PURE__*/React.createElement(StyledDialog, _extends({}, getTestAttributes('default'), {
    "aria-describedby": descriptionId.current,
    "aria-labelledby": titleId.current,
    "aria-modal": true,
    className: dialogClassName,
    ref: attachRefs,
    role: "dialog"
  }, rest), title && /*#__PURE__*/React.createElement(Heading, _extends({}, getTestAttributes('title'), {
    as: "h2",
    className: "csl-dialog__title",
    id: titleId.current,
    marginBottom: "03",
    size: "s4",
    tabIndex: -1
  }), title), /*#__PURE__*/React.createElement("div", {
    id: descriptionId.current
  }, children), closeButton && /*#__PURE__*/React.createElement(StyledCloseButton, _extends({}, getTestAttributes('closeButton'), {
    className: "csl-dialog__close-button",
    onClick: handleCloseButtonClick,
    title: "Close dialogue",
    type: "button"
  }), /*#__PURE__*/React.createElement(Close, {
    color: "inherit",
    size: "s1",
    iconSize: "md",
    tabIndex: undefined
  }))))))), brandContext.portalEl);
};
Dialog.displayName = 'Dialog';
export default Dialog;